import type { EfError } from '@form-factory/schema/contracts';
import { HTTPError } from '@toss/ky';
import { useCallback, useState } from 'react';

/**
 * Parse eFitness error response into EfError[]
 */
export async function parseEfError(error: unknown): Promise<EfError[]> {
  if (error instanceof HTTPError) {
    const response = await error.response.json();

    if (typeof response !== 'object' || !response) {
      return [
        {
          responseCode: 500,
          message: 'Unknown error',
          path: '',
        },
      ];
    }

    // Handle eFitness errors
    if ('errors' in response && Array.isArray(response.errors)) {
      return response.errors as EfError[];
    }

    // Handle our API errors
    if ('message' in response && 'statusCode' in response) {
      return [
        {
          responseCode: response.statusCode,
          message: response.message,
          path: '',
        },
      ];
    }
  }

  return [
    {
      responseCode: 500,
      message: 'Unknown error',
      path: '',
    },
  ];
}

/**
 * Helper hook to handle errors from eFitness API. If the error
 * is not a efitness error, it will be set as an unknown error.
 */
export function useEfErrorHandler() {
  const [errors, setErrors] = useState<EfError[]>([]);

  const handleError = useCallback(async (error: unknown) => {
    const efErrors = await parseEfError(error);
    console.error(efErrors);

    setErrors(efErrors);
  }, []);

  const resetErrors = useCallback(() => {
    setErrors([]);
  }, []);

  return { errors, handleError, resetErrors };
}
